import {
  IJourneyLegLoad,
  IJourneyRoute,
  ILeg,
  ITransportAssetConfiguration,
  ITransportAssetConfigurationAllocations,
  ITransportAssetSealAllocations,
  ITransportEmployeeAllocations,
} from '../Transport/types'
import { ILocation } from '../types'

export type JourneyLoad = {
  id: number
  shipmentBookingId: number
  loadDetailId: number
  quantity: number
  isHazardous: boolean
  contents: any[]
  updatedByUserId: number
  description?: string
  isDefined?: boolean
}

// JourneyLoadDto from API - matches backend JourneyLoadDto
export type JourneyLoadDto = {
  loadId: number
  shipmentBookingId: number
  quantity: number
  isHazardous: boolean
  description?: string
  trackingNumber?: string
  loadTypeId: number
  loadTypeName?: string
  customerId: number
  isTemperatureControlled: boolean
  temperatureSetPoint?: number
  shipFromLocationId?: number
  deliverToLocationId?: number
  waypointCount: number
  // Location names resolved via service calls
  shipFromLocationName?: string
  deliverToLocationName?: string
  customerName?: string
}

export enum JourneyStatusTypeId {
  Draft = 1,
  Planned = 2,
  Approved = 3,
  InProgress = 50,
  Completed = 100,
  Cancelled = 151,
}

export const JourneyStatusTypeLabels: Record<JourneyStatusTypeId, string> = {
  [JourneyStatusTypeId.Draft]: 'Draft',
  [JourneyStatusTypeId.Planned]: 'Planned',
  [JourneyStatusTypeId.Approved]: 'Approved',
  [JourneyStatusTypeId.InProgress]: 'In Progress',
  [JourneyStatusTypeId.Completed]: 'Completed',
  [JourneyStatusTypeId.Cancelled]: 'Cancelled',
}

export type Journey = {
  id: number
  tenantId: number
  legs: Leg[]
  isApproved: boolean
  name?: string
  description?: string
  journeyStatusTypeId: JourneyStatusTypeId
  createdOn?: string
}

export type Leg = {
  id: number
  journeyId: number
  carrierId?: number
  carrierWaybillMaster?: string
  sequence: number
  startJourneyWaypointId?: number
  endJourneyWaypointId?: number
  transportModeId?: number
  fulfilmentRequired: boolean
  routes: Route[]
  carrier?: Carrier
  status: string
}

export type Route = {
  id: number
  destinations: Destination[]
}

export type Destination = {
  loadActions?: LoadAction[]
  scheduledDate: string
  locationId: number
  location: ILocation
}

export type LoadAction = {
  id: number
  loadId: number
  load: JourneyLoad
  destinationActionTypeId: number
}

export type Carrier = {
  id: number
  name: string
}

export type JourneyFormType = {
  id?: number
  withLoads?: boolean
  isMultiModal?: boolean
  legs: ILeg[]
  assetConfigurationAllocations?: ITransportAssetConfigurationAllocations[]
  assetEmployeeAllocations: ITransportEmployeeAllocations[]
  assetSealAllocations: ITransportAssetSealAllocations[]
  loads: IJourneyLegLoad[]
  routes: IJourneyRoute[]
  assetConfigurations: ITransportAssetConfiguration[]
  assetLoadAllocations?: any[]
}

// Unassigned Consignments Types (for three-level expandable table)
export type UnassignedTransitDetail = {
  transitDetailId: number
  transportModeName?: string
  isCrossBorder?: boolean
  fulfilmentRequired: boolean
  preferredCarrierName?: string
  preBookedTransitIdentifier?: string
  scheduledDeparture?: string
  scheduledArrival?: string
}

export type UnassignedWaypoint = {
  waypointId: number
  locationId?: number
  waypointTypeName?: string
  locationName?: string
  contactName?: string
  instructions?: string
  clearingRequired: boolean
  services?: string
  sequence: number
  transitDetails?: UnassignedTransitDetail[]
}

export type UnassignedConsignment = {
  shipmentBookingId: number
  trackingNumber?: string
  loadTypeName?: string
  unassignedLoadCount: number
  temperatureControlledCount: number
  shipFromLocationName?: string
  waypointCount: number
  deliverToLocationName?: string
  customerName?: string
  // Consignment Information fields for side panel
  shipperName?: string
  currencyCode?: string
  referenceNumber?: string
  description?: string
  waypoints?: UnassignedWaypoint[]
  loads?: UnassignedLoad[]
}

export type UnassignedLoadContents = {
  id: number
  loadId: number
  description: string
  quantity: number
  unitPrice?: number
  total?: number
  countryOfOriginId?: number
  countryOfOriginName?: string
  // HS Code fields
  hsHeadingId?: number
  hsSubheadingId?: number
  // Physical Properties fields
  netWeight?: number
  grossWeight?: number
  volume?: number
  height?: number
  length?: number
  width?: number
  weightMeasureUnitId?: number
  volumeMeasureUnitId?: number
  lengthMeasureUnitId?: number
  // Temperature Setting fields
  temperatureRangeId?: number
  temperatureUnitId?: number
  setPoint?: number
  upperThresholdWarning?: number
  upperThresholdCritical?: number
  lowerThresholdWarning?: number
  lowerThresholdCritical?: number
}

export type UnassignedLoad = {
  loadId: number
  shipmentBookingId: number
  trackingNumber?: string
  quantity: number
  serviceWaypointLocations?: string
  services?: string
  loadTypeId?: number
  loadTypeName?: string
  temperatureRange?: string
  // Additional fields for Contents side panel
  value?: number
  description?: string
  isHazardous: boolean
  contents?: UnassignedLoadContents[]
}

// Types for View Consignment Services side panel (US-JP-010)
export type ConsignmentServiceLoad = {
  loadId: number
  trackingNumber: string
  numberOfLoads: number
  loadTypeName: string
  temperatureRange: string
}

export type ConsignmentServiceGroup = {
  locationId: number
  locationName: string
  serviceId: number
  serviceName: string
  loads: ConsignmentServiceLoad[]
}

// Types for Journey View (Read-Only) - US-JP-011
export type JourneyViewConsignment = {
  shipmentBookingId: number
  trackingNumber: string
  type: string
  quantity: number
  temperatureControlled: string
  shipFrom: string
  waypointCount: number
  deliverTo: string
  customer: string
  waypoints: JourneyViewWaypoint[]
}

export type JourneyViewWaypoint = {
  waypointId: number
  waypointType: string
  location: string
  contact: string
  specialInstructions: string
  clearingRequired: boolean
  services: string
  sequence: number
  transitDetails?: JourneyViewTransitDetail[]
}

export type JourneyViewTransitDetail = {
  transitDetailId: number
  modeOfTransit: string
  crossBorder: boolean
  fulfilmentRequired: boolean
  preferredCarrier: string
  preBookedTransitIdentifier: string
  scheduledDeparture: string
  scheduledArrival: string
}

export type JourneyViewRoute = {
  routeId: number
  routeNumber: number
  carrier: string
  carrierId?: number
  status: string
  transitIdentifier?: string
  scheduledDepartureDate?: string
  scheduledArrivalDate?: string
  destinations: JourneyViewDestination[]
}

export type JourneyViewDestination = {
  destinationId: number
  location: string
  locationId: number
  date: string
  action: string
  service: string
  consignments: string[]
}

export type JourneyViewLeg = {
  legId: number
  legNumber: number
  transportMode: string
  transportModeId?: number
  routes: JourneyViewRoute[]
}
